import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from 'components/UI/cn'

interface SidebarSectionProps {
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
  headerRight?: React.ReactNode
}

/**
 * Collapsible sidebar section with a divider extending from the title to the right edge.
 *
 * Designed for entity detail pages (deals, companies) where sections
 * are separated by dividers rather than wrapped in bordered cards.
 */
export function SidebarSection({
  title,
  children,
  defaultOpen = true,
  headerRight,
}: SidebarSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <div>
      <div className="flex items-center gap-2 py-3">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center gap-1.5 text-left focus:outline-none flex-shrink-0"
        >
          <ChevronDown
            size={14}
            className={cn(
              'text-brand transition-transform duration-200',
              !isOpen ? '-rotate-90' : ''
            )}
          />
          <span className="text-sm font-medium text-fg">{title}</span>
        </button>
        <div className="flex-1 h-px bg-line" />
        {headerRight && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {headerRight}
          </div>
        )}
      </div>
      {isOpen && <div className="pb-3 pl-5">{children}</div>}
    </div>
  )
}
